/**
 * Roof waste calculation utilities for contractors
 */

export interface WasteFactors {
  baseSquares: number;
  wastePercent: number;
  steepWastePercent: number;
  cutUpFactor: string;
  wasteSquares: number;
  totalSquaresWithWaste: number;
}

/**
 * Calculate waste factors based on roof characteristics
 */
export function calculateWaste(
  totalSquares: number,
  avgPitchRatio: number,
  facetCount: number
): WasteFactors {
  let baseWaste = 0.10; // 10% standard waste
  let steepWaste = 0;
  
  // Steep pitch waste (7/12 or steeper gets extra 10%)
  if (avgPitchRatio >= 7) {
    steepWaste = 0.10;
    baseWaste += steepWaste;
  }

  // Cut-up waste based on complexity (number of facets)
  let cutUpFactor = "Low";
  if (facetCount >= 8) {
    baseWaste += 0.05;
    cutUpFactor = "Moderate";
  }
  if (facetCount >= 12) {
    baseWaste += 0.10;
    cutUpFactor = "High";
  }

  const wasteSquares = totalSquares * baseWaste;
  const totalWithWaste = totalSquares + wasteSquares;

  return {
    baseSquares: totalSquares,
    wastePercent: Math.round(baseWaste * 100),
    steepWastePercent: Math.round(steepWaste * 100),
    cutUpFactor,
    wasteSquares: Math.round(wasteSquares * 10) / 10,
    totalSquaresWithWaste: Math.round(totalWithWaste * 10) / 10,
  };
}

/**
 * Get average pitch ratio from planes
 */
export function getAveragePitchRatio(planes: Array<{ tiltDegrees?: number | null }>): number {
  if (!planes || planes.length === 0) return 0;
  
  const totalRise = planes.reduce((sum, plane) => {
    // Defensive check: ensure tiltDegrees exists and is a valid number
    if (typeof plane.tiltDegrees !== 'number' || isNaN(plane.tiltDegrees)) {
      return sum;
    }
    const rise = Math.tan((plane.tiltDegrees * Math.PI) / 180) * 12;
    return sum + Math.round(rise);
  }, 0);
  
  const validPlanes = planes.filter(p => typeof p.tiltDegrees === 'number' && !isNaN(p.tiltDegrees)).length;
  return validPlanes > 0 ? Math.round(totalRise / validPlanes) : 0;
}
